// Single source of truth for the tickets table column set. The toolbar's
// "Columns" picker, the table header, and the body cells all key off
// this list — keep them in lockstep by editing here.

export type ColumnKey =
  | "ticket_id"
  | "subject"
  | "requester"
  | "queue"
  | "priority"
  | "status"
  | "assignee"
  | "created_at"
  | "escalated_at"
  | "last_response"
  | "sla"

export type ColumnDef = {
  key: ColumnKey
  label: string
  // null = not sortable. Otherwise the param value the server expects.
  sortKey:
    | "created_at"
    | "initiated_at"
    | "last_message_at"
    | "priority"
    | "state"
    | "sla_next_due_at"
    | "escalated_at"
    | null
}

export const COLUMNS: ColumnDef[] = [
  { key: "ticket_id",     label: "Ticket ID",     sortKey: null },
  { key: "subject",       label: "Subject",       sortKey: null },
  { key: "requester",     label: "Requester",     sortKey: null },
  { key: "queue",         label: "Queue",         sortKey: null },
  { key: "priority",      label: "Priority",      sortKey: "priority" },
  { key: "status",        label: "Status",        sortKey: "state" },
  { key: "assignee",      label: "Assignee",      sortKey: null },
  { key: "created_at",    label: "Created at",    sortKey: "created_at" },
  { key: "escalated_at",  label: "Escalated at",  sortKey: "escalated_at" },
  { key: "last_response", label: "Last response", sortKey: "last_message_at" },
  { key: "sla",           label: "SLA",           sortKey: "sla_next_due_at" },
]

export const DEFAULT_VISIBLE: ColumnKey[] = COLUMNS.map((c) => c.key)

const STORAGE_KEY = "tickets:visibleColumns"

export function loadVisibleColumns(): ColumnKey[] {
  if (typeof window === "undefined") return DEFAULT_VISIBLE
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_VISIBLE
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE
    const valid = parsed.filter((k): k is ColumnKey =>
      COLUMNS.some((c) => c.key === k),
    )
    return valid.length > 0 ? valid : DEFAULT_VISIBLE
  } catch {
    return DEFAULT_VISIBLE
  }
}

export function saveVisibleColumns(cols: ColumnKey[]): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cols))
  } catch {
    // localStorage can fail in private mode / quota exceeded — silent.
  }
}
